// src/pages/auth/pages/SignupPage.jsx
import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ReactComponent as UserIcon } from "../../../../assets/icons/fi-user.svg";
import { ReactComponent as CompanyIcon } from "../../../../assets/icons/building.svg";
import { userService } from "../../../../services/api/authenService";
import { toast } from "react-toastify";
import PersonalSignup from "./PersonalSignup";
import CompanySignup from "./CompanySignup";

const SignupPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("personal");
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    // personal
    fullName: "",
    phone: "",
    email: "",
    // common
    password: "",
    confirmPassword: "",
    agreedToTerms: false,
    // company
    companyName: "",
    tax: "",
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [focusField, setFocusField] = useState("");

  const handleFocus = (field) => setFocusField(field);
  const handleBlur = () => setFocusField("");

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target || e; // cho cả custom call từ popup
    setFormData((s) => ({
      ...s,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((s) => ({ ...s, [name]: "" }));
  };

  // Validators
  const validateEmail = (v) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v ?? "").trim());
  const validatePhoneVN = (v) =>
    /^(03|05|07|08|09)\d{8}$/.test((v ?? "").trim());
  const strongEnough = (v) => (v ?? "").length >= 6;

  const validatePersonalForm = () => {
    const err = {};
    if (!formData.fullName.trim()) err.fullName = "Họ tên không được để trống.";
    if (!validatePhoneVN(formData.phone))
      err.phone = "SĐT phải 10 số, bắt đầu 03,05,07,08,09.";
    if (!validateEmail(formData.email)) err.email = "Email không hợp lệ.";
    if (!strongEnough(formData.password)) err.password = "Mật khẩu ≥ 6 ký tự.";
    if (formData.password !== formData.confirmPassword)
      err.confirmPassword = "Mật khẩu không khớp.";
    if (!formData.agreedToTerms)
      err.agreedToTerms = "Bạn phải đồng ý điều khoản.";
    return err;
  };

  const validateCompanyForm = () => {
    const err = {};
    if (!formData.companyName.trim())
      err.companyName = "Tên doanh nghiệp không được để trống.";
    if (!formData.tax.trim()) err.tax = "Mã số thuế không được để trống.";
    if (!validateEmail(formData.email)) err.email = "Email không hợp lệ.";
    if (!strongEnough(formData.password)) err.password = "Mật khẩu ≥ 6 ký tự.";
    if (formData.password !== formData.confirmPassword)
      err.confirmPassword = "Mật khẩu không khớp.";
    if (!formData.agreedToTerms)
      err.agreedToTerms = "Bạn phải đồng ý điều khoản.";
    return err;
  };

  const onSubmitPersonal = async (e) => {
    e.preventDefault();
    const err = validatePersonalForm();
    if (Object.keys(err).length) {
      setErrors(err);
      return;
    }
    setIsLoading(true);
    try {
      const payload = {
        type: "individual",
        fullName: formData.fullName.trim(),
        phoneNumber: formData.phone.trim(),
        email: formData.email.trim(),
        password: formData.password,
      };
      await userService.register(payload);
      toast.success("Đăng ký thành công!");
      // điều hướng ở route cha (AppRoutes) hay ở đây tùy bạn
      navigate("/login");
    } catch (error) {
      toast.dismiss();
      toast.error(error?.response?.data?.message || "Đăng ký thất bại!");
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitCompany = async (e) => {
    e.preventDefault();
    const err = validateCompanyForm();
    if (Object.keys(err).length) {
      setErrors(err);
      return;
    }
    setIsLoading(true);
    try {
      const payload = {
        type: "company",
        companyName: formData.companyName.trim(),
        taxCode: formData.tax.trim(),
        email: formData.email.trim(),
        password: formData.password,
      };
      await userService.register(payload);
      toast.success("Đăng ký thành công!");
      navigate("/login");
    } catch (error) {
      toast.dismiss();
      toast.error(error?.response?.data?.message || "Đăng ký thất bại!");
    } finally {
      setIsLoading(false);
    }
  };

  const activeIdx = activeTab === "personal" ? 0 : 1;
  const indicatorStyle = useMemo(
    () => ({
      transform: `translateX(${activeIdx * 100}%)`,
    }),
    [activeIdx]
  );

  return (
    <div className="w-full h-full flex flex-col items-center justify-center">
      {/* Header mobile */}
      <div className="flex desktop:hidden flex-col items-center justify-center mb-3 space-y-1 animate-fade-in-up">
        <div className="flex items-center justify-center">
          <img
            src="/logos/logo_vector.svg"
            alt="Web logo"
            className="size-[46px] 3xl:size-[57px]"
          />
          <h1 className="font-be-vietnam-semiBold font-bold text-[34px] block">
            Sohuutritue.vn
          </h1>
        </div>
        <p className="text-center max-w-xl">Nền tảng số Sở hữu trí tuệ</p>
      </div>

      {/* Header desktop */}
      <div className="flex items-center animate-fade-in">
        <img
          src="/logos/logo_vector.svg"
          alt="Web logo"
          className="size-[36px] 3xl:size-[57px] desktop:block hidden 3xl:hidden"
        />
        <h1 className="text-2xl font-be-vietnam-semiBold font-bold ml-2">
          Đăng ký
        </h1>
      </div>

      {/* Tabs */}
      <div className="w-full  mt-2">
        <div className="relative border-b">
          <div className="grid grid-cols-2">
            <button
              type="button"
              onClick={(e) => {
                setActiveTab("personal");
                // ripple
                const r = document.createElement("span");
                r.className = "ripple";
                const rect = e.currentTarget.getBoundingClientRect();
                r.style.left = `${e.clientX - rect.left}px`;
                r.style.top = `${e.clientY - rect.top}px`;
                e.currentTarget.appendChild(r);
                setTimeout(() => r.remove(), 450);
              }}
              className={`tab-btn ${activeTab === "personal"
                  ? "text-blue-600 font-bold"
                  : "text-gray-500"
                }`}
            >
              <UserIcon />
              <span>Cá nhân</span>
            </button>
            <button
              type="button"
              onClick={(e) => {
                setActiveTab("company");
                const r = document.createElement("span");
                r.className = "ripple";
                const rect = e.currentTarget.getBoundingClientRect();
                r.style.left = `${e.clientX - rect.left}px`;
                r.style.top = `${e.clientY - rect.top}px`;
                e.currentTarget.appendChild(r);
                setTimeout(() => r.remove(), 450);
              }}
              className={`tab-btn ${activeTab === "company"
                  ? "text-blue-600 font-bold"
                  : "text-gray-500"
                }`}
            >
              <CompanyIcon />
              <span>Công ty</span>
            </button>
          </div>

          {/* Indicator */}
          <div
            className="absolute bottom-0 left-0 h-[2px] w-1/2 bg-blue-500 transition-transform duration-300"
            style={indicatorStyle}
          />
        </div>
      </div>

      {/* Body (cross-fade by key) */}
      <div key={activeTab} className="w-full  mt-3 animate-fade-in">
        {activeTab === "personal" ? (
          <PersonalSignup
            formData={formData}
            handleChange={handleChange}
            handleSubmit={onSubmitPersonal}
            errors={errors}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
            showConfirmPassword={showConfirmPassword}
            setShowConfirmPassword={setShowConfirmPassword}
            focusField={focusField}
            handleFocus={handleFocus}
            handleBlur={handleBlur}
            isLoading={isLoading}
          />
        ) : (
          <CompanySignup
            formData={formData}
            handleChange={handleChange}
            handleSubmit={onSubmitCompany}
            errors={errors}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
            showConfirmPassword={showConfirmPassword}
            setShowConfirmPassword={setShowConfirmPassword}
            focusField={focusField}
            handleFocus={handleFocus}
            handleBlur={handleBlur}
            isLoading={isLoading}
          />
        )}
      </div>

      <div
        className="mt-4 text-sm opacity-0 animate-fade-in"
        style={{ animationDelay: "120ms", animationFillMode: "forwards" }}
      >
        Đã có tài khoản?{" "}
        <Link to="/login" className="text-blue-500 hover:underline">
          Đăng nhập
        </Link>
      </div>

      <style>{`
        .tab-btn {
          position: relative;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          justify-content: center;
          padding: 12px 10px;
          transition: color 220ms ease;
          overflow: hidden;
        }
        .ripple {
          position: absolute;
          width: 10px; height: 10px;
          transform: translate(-50%, -50%);
          border-radius: 9999px;
          background: rgba(59,130,246,0.25);
          animation: ripple 450ms ease-out forwards;
          pointer-events: none;
        }
        @keyframes ripple {
          from { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          to   { opacity: 0; transform: translate(-50%, -50%) scale(12); }
        }

        .animate-fade-in { animation: fadeIn .35s ease forwards; }
        .animate-fade-in-up { animation: fadeInUp .40s cubic-bezier(0.22,1,0.36,1) forwards; }

        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(8px) } to { opacity: 1; transform: translateY(0) } }
      `}</style>
    </div>
  );
};

export default SignupPage;
